import { isDarkTheme } from '..';
import { BuildClass, fsmData } from '../../../universal';
import { React, _ } from '../../lib';
import { setContextMenu } from './context-menu';
import { CJSX, useStateSync } from './meta-types';
import { stubDiv } from './stubs';
export const ResizeSplitter = React.forwardRef((props, ref) => {
    const elContainer = React.useRef(stubDiv);
    const cacheRect = React.useRef(stubDiv.getBoundingClientRect());
    React.useEffect(() => {
        const runObserverEvent = () => {
            cacheRect.current =
                elContainer.current?.getBoundingClientRect() ?? cacheRect.current;
            updateWidths(widths, null, cacheRect.current.width);
        };
        const observer = new ResizeObserver(runObserverEvent);
        observer.observe(elContainer.current);
        runObserverEvent();
        return () => {
            observer.disconnect();
        };
    }, [elContainer.current]);
    const [widths, setWidths] = React.useState(props.value ?? null);
    useStateSync({
        propVal: props.value ?? null,
        setProp: props.onUpdate,
        stateVal: widths,
        setState: setWidths,
        compareFn: (p, s) => _.isEqual(p, s),
    });
    const [minMaxWidths, setMinMaxWidths] = React.useState(() => getMinMaxWidths(widths, cacheRect.current.width, props.panes));
    React.useEffect(() => {
        const totalWidth = cacheRect.current.width;
        if (!widths) {
            return;
        }
        const newWidths = props.panes.map((pane, index) => {
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? totalWidth;
            let width = widths[index] ?? -1;
            if (!(pane.collapsible && width == 0) && width < minWidth) {
                width = Math.max(width, minWidth);
            }
            return Math.min(width, maxWidth);
        });
        if (!_.isEqual(newWidths, widths)) {
            updateWidths(newWidths);
        }
        const newMinMaxWidths = getMinMaxWidths(newWidths, cacheRect.current.width, props.panes);
        if (!_.isEqual(newMinMaxWidths, minMaxWidths)) {
            setMinMaxWidths(newMinMaxWidths);
        }
    }, [cacheRect.current.width, widths, props.panes, props.value]);
    const updateWidths = (newWidths, index, containerWidth) => {
        const newWidthsSanitised = calculateNewWidths(newWidths, containerWidth ?? cacheRect.current.width, props.panes, index ?? null);
        if (!_.isEqual(widths, newWidthsSanitised)) {
            setWidths(newWidthsSanitised);
        }
    };
    React.useEffect(() => {
        updateWidths(widths);
    }, [
        cacheRect.current.width,
        elContainer.current,
        widths,
        props.panes,
        props.value,
    ]);
    const [dragging, setDragging] = React.useState(null);
    React.useImperativeHandle(ref, () => ({
        getElement: () => elContainer.current,
        reset: () => {
            updateWidths(null);
        },
    }));
    if (!minMaxWidths || !widths) {
        return (React.createElement("div", { ref: elContainer, className: BuildClass({
                'ui5-resize-splitter': true,
                [props.className ?? '']: true,
                loading: true,
            }) }));
    }
    return (React.createElement("div", { ref: elContainer, className: BuildClass({
            'ui5-resize-splitter': true,
            [props.className ?? '']: true,
        }) },
        React.createElement("div", { className: BuildClass({
                'resize-splitter-backdrop': true,
                active: dragging != null,
            }) }),
        props.panes.map((pane, index) => (React.createElement(React.Fragment, { key: index },
            React.createElement("div", { className: "ui5-resize-splitter-pane", style: {
                    width: `${widths[index]}px`,
                } }, pane.content),
            React.createElement(CJSX, { cond: index < props.panes.length - 1 },
                React.createElement(ResizeSplitterHandle, { borderColor: props.borderColor, pane: pane, isDragging: dragging == index, width: widths[index], minMaxWidth: minMaxWidths[index], isCollapsed: widths[index] == 0 && pane.collapsible, onDragStart: () => {
                        setDragging(index);
                    }, onDragEnd: newWidth => {
                        setDragging(null);
                        const newWidths = [...widths];
                        newWidths[index] = newWidth;
                        updateWidths(newWidths, index);
                    }, onUncollapse: () => {
                        const newWidths = [...widths];
                        newWidths[index] = pane.defaultWidth;
                        updateWidths(newWidths);
                    }, onResetWidths: () => {
                        updateWidths(null);
                    } })))))));
});
const ResizeSplitterHandle = (props) => (React.createElement("div", { className: "ui5-resize-splitter-divider-wrapper" },
    React.createElement("div", { className: "splitter-border", style: {
            backgroundColor: props.borderColor ??
                (isDarkTheme() ? 'hsl(0, 0%, 28%)' : '#aaaaaa'),
        } }),
    React.createElement("div", { className: BuildClass({
            'ui5-resize-splitter-collapsed-handle': true,
            hidden: !props.isCollapsed,
        }), title: "Click to reset the collapsed pane to its default width", onClick: () => {
            props.onUncollapse();
        } },
        React.createElement("img", { className: "arrow-img", alt: "Reset pane widths", src: "/static/img/svg/cb-arrow.svg" })),
    React.createElement("div", { className: BuildClass({
            'ui5-resize-splitter-divider': true,
            active: props.isDragging,
        }), onMouseDown: e => {
            const element = e.currentTarget;
            const offset = e.clientX;
            props.onDragStart();
            document.body.style.userSelect = 'none';
            const minMax = props.minMaxWidth;
            const minOffset = minMax.minWidth - props.width;
            const maxOffset = minMax.maxWidth - props.width;
            const collapsible = props.pane.collapsible ?? false;
            const collapse_threshold = (minOffset - props.width) / 2;
            const mouseMoveHandler = (e1) => {
                let transX = e1.clientX - offset;
                if (collapsible && transX < collapse_threshold) {
                    transX = -props.width;
                }
                else if (transX < minOffset) {
                    transX = minOffset;
                }
                else if (transX > maxOffset) {
                    transX = maxOffset;
                }
                element.style.transform = `translate(${transX}px, 0px)`;
            };
            const mouseUpHandler = (e1) => {
                let transX = e1.clientX - offset;
                let collapsed = false;
                if (collapsible && transX < collapse_threshold) {
                    transX = -props.width;
                    collapsed = true;
                }
                else if (transX < minOffset) {
                    transX = minOffset;
                }
                else if (transX > maxOffset) {
                    transX = maxOffset;
                }
                element.style.transform = null;
                document.body.style.userSelect = undefined;
                document.removeEventListener('mousemove', mouseMoveHandler);
                document.removeEventListener('mouseup', mouseUpHandler);
                const newWidth = collapsed ? 0 : props.width + transX;
                props.onDragEnd(newWidth);
            };
            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
        }, onContextMenu: e => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu({
                position: {
                    x: e.clientX,
                    y: e.clientY,
                },
                items: [
                    {
                        label: 'Reset Pane Widths',
                        onClick: () => {
                            props.onResetWidths();
                        },
                    },
                ],
            });
        } })));
const getMinMaxWidths = (widths, containerWidth, panes) => {
    if (!widths) {
        return null;
    }
    const ranges = panes.map((pane, index) => {
        const minWidth = pane.minWidth ?? 0;
        const maxWidth = pane.maxWidth ?? containerWidth;
        const width = widths[index] ?? -1;
        return {
            index: index,
            contract: width - minWidth,
            expand: maxWidth - width,
        };
    });
    const realMinMax = panes.map((_pane, index) => {
        const thisRange = ranges[index] ?? { index: -1, contract: -1, expand: -1 };
        const availableContract = _.sum(fsmData(ranges, {
            filter: x => x.index != index,
            map: x => x.expand,
        }));
        const availableExpand = _.sum(fsmData(ranges, {
            filter: x => x.index != index,
            map: x => x.contract,
        }));
        const width = widths[index] ?? -1;
        return {
            index: index,
            minWidth: width - Math.min(thisRange.contract, availableContract),
            maxWidth: width + Math.min(thisRange.expand, availableExpand),
        };
    });
    return realMinMax;
};
const calculateDefaultWidths = (containerWidth, panes) => {
    const nonFlexiWidth = _.sum(fsmData(panes, {
        filter: x => x.flexWidth == null,
        map: x => x.defaultWidth,
    }));
    const remainingWidth = Math.max(containerWidth - nonFlexiWidth, 0);
    const flexiSum = _.sum(fsmData(panes, {
        filter: x => x.flexWidth != null,
        map: x => x.flexWidth,
    }));
    const widthPerFlex = remainingWidth / flexiSum;
    return calculateNewWidths(panes.map(pane => {
        if (pane.flexWidth != null) {
            return Math.floor(widthPerFlex * pane.flexWidth);
        }
        return pane.defaultWidth;
    }), containerWidth, panes, null);
};
const calculateNewWidths = (widths, containerWidth, panes, lastResizedIndex, recursiveCounter = 0) => {
    if (!widths && containerWidth == 0) {
        return null;
    }
    widths ??= calculateDefaultWidths(containerWidth, panes);
    const currentTotalWidth = _.sum(widths.map(x => x ?? 0));
    const deficit = containerWidth - currentTotalWidth;
    if (containerWidth == 0) {
        return widths;
    }
    const panesWithIndex = _.map(panes, (x, i) => ({ ...x, index: i }));
    let deficitRemaining = deficit;
    let remainingFlexiPanes = panesWithIndex.filter(x => x.flexWidth != null && x.index != lastResizedIndex);
    const newWidths = widths.map(x => x ?? 0);
    let passes = 0;
    while (deficitRemaining != 0 && remainingFlexiPanes.length > 0 && passes < 20) {
        const totalFlexWidth = _.sum(remainingFlexiPanes.map(x => x.flexWidth ?? 0));
        const proportions = remainingFlexiPanes.map(pane => {
            const width = widths[pane.index];
            const this_pane_proportion = (pane.flexWidth ?? 0) / totalFlexWidth;
            const fullDiff = deficitRemaining * this_pane_proportion;
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? containerWidth;
            if (deficitRemaining > 0) {
                return Math.min((maxWidth - width) / fullDiff, 1);
            }
            if (deficitRemaining < 0) {
                return Math.min((minWidth - width) / fullDiff, 1);
            }
            return 1;
        });
        const proportion = _.min(proportions) ?? 0;
        remainingFlexiPanes.forEach(pane => {
            const width = widths[pane.index];
            const this_pane_proportion = (pane.flexWidth ?? 0) / totalFlexWidth;
            const diff = deficitRemaining * proportion * this_pane_proportion;
            const newWidth = Math.round(width + diff);
            deficitRemaining -= newWidth - width;
            newWidths[pane.index] = newWidth;
        });
        remainingFlexiPanes = remainingFlexiPanes.filter(pane => proportions[pane.index] != proportion);
        passes++;
    }
    let remainingNonFlexiPanes = panesWithIndex.filter(x => x.flexWidth == null);
    passes = 0;
    while (deficitRemaining != 0 && remainingNonFlexiPanes.length > 0 && passes < 20) {
        const proportions = remainingNonFlexiPanes.map(pane => {
            const width = widths[pane.index];
            const this_pane_proportion = 1 / remainingNonFlexiPanes.length;
            const fullDiff = deficitRemaining * this_pane_proportion;
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? containerWidth;
            if (deficitRemaining > 0) {
                return Math.min((maxWidth - width) / fullDiff, 1);
            }
            if (deficitRemaining < 0) {
                return Math.min((minWidth - width) / fullDiff, 1);
            }
            return 1;
        });
        const proportion = _.min(proportions) ?? 0;
        remainingNonFlexiPanes.forEach(pane => {
            const width = widths[pane.index];
            const this_pane_proportion = 1 / remainingNonFlexiPanes.length;
            const diff = deficitRemaining * proportion * this_pane_proportion;
            const newWidth = Math.floor(width + diff);
            deficitRemaining -= newWidth - width;
            newWidths[pane.index] = newWidth;
        });
        remainingNonFlexiPanes = remainingNonFlexiPanes.filter(pane => proportions[pane.index] != proportion);
        passes++;
    }
    const lastResizedPane = panes[lastResizedIndex ?? -1];
    if (deficitRemaining && lastResizedIndex && lastResizedPane?.flexWidth) {
        const width = widths[lastResizedIndex] ?? -1;
        const minWidth = lastResizedPane.minWidth ?? 0;
        const maxWidth = lastResizedPane.maxWidth ?? containerWidth;
        const newWidth = Math.min(Math.max(width + deficitRemaining, minWidth), maxWidth);
        newWidths[lastResizedIndex] = newWidth;
        deficitRemaining -= newWidth - width;
    }
    if (deficitRemaining == 0) {
        return newWidths;
    }
    if (recursiveCounter < 5) {
        return calculateNewWidths(newWidths, containerWidth, panes, lastResizedIndex, recursiveCounter + 1);
    }
    console.warn(`ResizeSplitter deficit: ${deficitRemaining}`);
    return newWidths;
};
//# sourceMappingURL=resize-split.js.map